import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Extract a readable error message from an API error response
 * Handles various formats: string, array of validation errors, object with msg/message
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  const errorDetail = error?.response?.data?.detail;
  if (!errorDetail) return fallback;
  
  if (typeof errorDetail === 'string') {
    return errorDetail;
  }
  
  if (Array.isArray(errorDetail)) {
    return errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
  }
  
  if (typeof errorDetail === 'object') {
    return errorDetail.msg || errorDetail.message || fallback;
  }
  
  return fallback;
};

export const api = {
  // Authentication
  auth: {
    forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
    validateResetToken: (token) => axios.get(`${API}/auth/validate-reset-token?token=${token}`),
    resetPassword: (token, password) => axios.post(`${API}/auth/reset-password`, { token, password }),
  },
  clients: {
    getAll: () => axios.get(`${API}/clients`),
    getById: (id) => axios.get(`${API}/clients/${id}`),
    create: (data) => axios.post(`${API}/clients`, data),
    update: (id, data) => axios.put(`${API}/clients/${id}`, data),
    delete: (id) => axios.delete(`${API}/clients/${id}`),
    uploadLogo: (id, formData) => axios.post(`${API}/clients/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    // Client Dashboard
    getDashboard: (id, days = 30) => axios.get(`${API}/clients/${id}/dashboard?days=${days}`),
    // Branches
    addBranch: (id, data) => axios.post(`${API}/clients/${id}/branches`, data),
    updateBranch: (id, branchId, data) => axios.put(`${API}/clients/${id}/branches/${branchId}`, data),
    deleteBranch: (id, branchId) => axios.delete(`${API}/clients/${id}/branches/${branchId}`),
  },
  projects: {
    getAll: () => axios.get(`${API}/projects`),
    getById: (id) => axios.get(`${API}/projects/${id}`),
    create: (data) => axios.post(`${API}/projects`, data),
    update: (id, data) => axios.put(`${API}/projects/${id}`, data),
    delete: (id) => axios.delete(`${API}/projects/${id}`),
    getDocuments: (id) => axios.get(`${API}/projects/${id}/documents`),
    uploadDocument: (id, data) => axios.post(`${API}/projects/${id}/documents`, data),
    getSummary: (id) => axios.get(`${API}/projects/${id}/summary`),
  },
  // Professional Team (Consultants)
  professionalTeam: {
    getAll: (projectId) => axios.get(`${API}/projects/${projectId}/professional-team`),
    getById: (projectId, memberId) => axios.get(`${API}/projects/${projectId}/professional-team/${memberId}`),
    create: (projectId, data) => axios.post(`${API}/projects/${projectId}/professional-team`, data),
    update: (projectId, memberId, data) => axios.put(`${API}/projects/${projectId}/professional-team/${memberId}`, data),
    delete: (projectId, memberId) => axios.delete(`${API}/projects/${projectId}/professional-team/${memberId}`),
  },
  // Internal Team
  internalTeam: {
    getAll: (projectId) => axios.get(`${API}/projects/${projectId}/internal-team`),
    create: (projectId, data) => axios.post(`${API}/projects/${projectId}/internal-team`, data),
    update: (projectId, memberId, data) => axios.put(`${API}/projects/${projectId}/internal-team/${memberId}`, data),
    delete: (projectId, memberId) => axios.delete(`${API}/projects/${projectId}/internal-team/${memberId}`),
  },
  // Distribution Rules
  distributionRules: {
    getAll: (projectId) => axios.get(`${API}/projects/${projectId}/distribution-rules`),
    create: (projectId, data) => axios.post(`${API}/projects/${projectId}/distribution-rules`, data),
    update: (projectId, ruleId, data) => axios.put(`${API}/projects/${projectId}/distribution-rules/${ruleId}`, data),
    delete: (projectId, ruleId) => axios.delete(`${API}/projects/${projectId}/distribution-rules/${ruleId}`),
  },
  // Email History & Mailto
  email: {
    generateMailto: (projectId, documentType, documentId, distribution) =>
      axios.post(`${API}/email/generate-mailto`, distribution, {
        params: { project_id: projectId, document_type: documentType, document_id: documentId }
      }),
    markSent: (historyId) => axios.post(`${API}/email/history/${historyId}/mark-sent`),
    recordResponse: (historyId, notes) => 
      axios.post(`${API}/email/history/${historyId}/response`, null, { params: { response_notes: notes } }),
    getHistoryForDocument: (documentType, documentId) => 
      axios.get(`${API}/email/history/document/${documentType}/${documentId}`),
    getHistoryForProject: (projectId) => axios.get(`${API}/email/history/project/${projectId}`),
    getHistoryDetail: (historyId) => axios.get(`${API}/email/history/${historyId}`),
    deleteHistory: (historyId) => axios.delete(`${API}/email/history/${historyId}`),
  },
  // Correspondence (Email-to-Event & Submission)
  correspondence: {
    // Submission flow
    submit: (projectId, documentType, documentId, distribution) => 
      axios.post(`${API}/correspondence/submit`, distribution, { 
        params: { project_id: projectId, document_type: documentType, document_id: documentId } 
      }),
    getLogs: (projectId) => axios.get(`${API}/projects/${projectId}/correspondence-logs`),
    getLog: (logId) => axios.get(`${API}/correspondence-logs/${logId}`),
    // Email-to-Event
    getEventEmailAddress: (eventId) => axios.get(`${API}/events/${eventId}/email-address`),
    add: (eventId, data) => axios.post(`${API}/events/${eventId}/correspondence`, data),
    getByEvent: (eventId, params = {}) => axios.get(`${API}/events/${eventId}/correspondence`, { params }),
    update: (messageId, data) => axios.put(`${API}/correspondence/${messageId}`, data),
    delete: (messageId) => axios.delete(`${API}/correspondence/${messageId}`),
    // Attachments
    uploadAttachment: (correspondenceId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/correspondence/${correspondenceId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    deleteAttachment: (correspondenceId, attachmentId) => 
      axios.delete(`${API}/correspondence/${correspondenceId}/attachments/${attachmentId}`),
    // Narrative & Summary
    generateNarrative: (eventId) => axios.post(`${API}/events/${eventId}/generate-narrative`, null, { timeout: 120000 }),
    getNarrative: (eventId) => axios.get(`${API}/events/${eventId}/narrative`),
    generateSummary: (eventId) => axios.post(`${API}/events/${eventId}/generate-summary`, null, { timeout: 120000 }),
    getRegister: (eventId) => axios.get(`${API}/events/${eventId}/correspondence-register`),
    // For Notice/Claim
    getForNotice: (noticeId) => axios.get(`${API}/notices/${noticeId}/correspondence`),
    getForClaim: (claimId) => axios.get(`${API}/claims/${claimId}/correspondence`),
    getAuditLog: (eventId) => axios.get(`${API}/events/${eventId}/audit-log`),
    // Mailgun webhook status
    getMailgunStatus: () => axios.get(`${API}/webhook/mailgun/status`),
    // Email Import & Management (from email-to-event correspondence)
    importEmail: (projectId, file, moduleType, moduleEntityId) => {
      const formData = new FormData();
      formData.append('file', file);
      let url = `${API}/correspondence/import?project_id=${projectId}`;
      if (moduleType) url += `&module_type=${moduleType}`;
      if (moduleEntityId) url += `&module_entity_id=${moduleEntityId}`;
      return axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    getEmails: (params = {}) => axios.get(`${API}/correspondence/emails`, { params }),
    getEmail: (id) => axios.get(`${API}/correspondence/emails/${id}`),
    updateEmail: (id, data) => axios.put(`${API}/correspondence/emails/${id}`, data),
    deleteEmail: (id) => axios.delete(`${API}/correspondence/emails/${id}`),
    analyzeEmail: (id) => axios.post(`${API}/correspondence/emails/${id}/analyze`),
    getStats: (projectId) => axios.get(`${API}/correspondence/stats/${projectId}`),
    downloadEmail: (id) => axios.get(`${API}/correspondence/emails/${id}/download`, { responseType: 'blob' }),
    downloadAttachment: (id) => axios.get(`${API}/correspondence/attachments/${id}/download`, { responseType: 'blob' }),
    // Correspondence detail & downloads
    getCorrespondenceDetail: (id) => axios.get(`${API}/correspondence/${id}/detail`),
    downloadCorrespondenceEml: (id) => axios.get(`${API}/correspondence/${id}/download-eml`, { responseType: 'blob' }),
    downloadCorrespondenceAttachment: (correspondenceId, attachmentId) => 
      axios.get(`${API}/correspondence/${correspondenceId}/attachments/${attachmentId}`, { responseType: 'blob' }),
    // AI analysis
    analyzeModuleCorrespondence: (moduleType, entityId) => 
      axios.post(`${API}/modules/${moduleType}/${entityId}/analyze-correspondence`, null, { timeout: 120000 }),
    generateSendToTaskToken: (projectId, moduleType, moduleEntityId, moduleReference) => 
      axios.post(`${API}/correspondence/send-to-task/generate`, null, {
        params: { project_id: projectId, module_type: moduleType, module_entity_id: moduleEntityId, module_reference: moduleReference }
      }),
  },
  // Disciplines
  disciplines: {
    getAll: () => axios.get(`${API}/disciplines`),
  },
  events: {
    getByProject: (projectId, params = {}) => axios.get(`${API}/projects/${projectId}/events`, { params }),
    getPendingApproval: (projectId) => axios.get(`${API}/projects/${projectId}/events/pending-approval`),
    getPendingClassification: (projectId) => axios.get(`${API}/projects/${projectId}/events/pending-classification`),
    getById: (id) => axios.get(`${API}/events/${id}`),
    create: (projectId, data) => axios.post(`${API}/projects/${projectId}/events`, data),
    update: (id, data) => axios.put(`${API}/events/${id}`, data),
    delete: (id) => axios.delete(`${API}/events/${id}`),
    approve: (id) => axios.post(`${API}/events/${id}/approve`),
    reject: (id, reason) => axios.post(`${API}/events/${id}/reject`, null, { params: { reason } }),
    classify: (id, data) => axios.put(`${API}/events/${id}/classify`, data),
    checkNoticeReadiness: (id) => axios.get(`${API}/events/${id}/notice-readiness`),
    getTaxonomy: () => axios.get(`${API}/event-taxonomy`),
    getClauseOptions: (projectId, context = 'notice') => axios.get(`${API}/projects/${projectId}/clause-options`, { params: { context } }),
    getClauseSuggestions: (contractType, eventFamily) => axios.get(`${API}/events/clause-suggestions`, { params: { contract_type: contractType, event_family: eventFamily } }),
    migrateTaxonomy: () => axios.post(`${API}/events/migrate-taxonomy`),
    getReport: (projectId) => axios.get(`${API}/projects/${projectId}/events/report`),
    getEventConfig: () => axios.get(`${API}/admin/event-config`),
    updateEventConfig: (data) => axios.put(`${API}/admin/event-config`, data),
    convertToNotice: (id, contractClause) => axios.post(`${API}/events/${id}/convert-to-notice`, null, { params: contractClause ? { contract_clause: contractClause } : {}, timeout: 120000 }), // 2 min timeout for AI generation
    markCeased: (id, ceasedDate, description = null, attachments = []) => {
      // If only ceasedDate provided (legacy), use query param
      if (!description && attachments.length === 0) {
        return axios.post(`${API}/events/${id}/mark-ceased`, null, { params: { ceased_date: ceasedDate } });
      }
      // Otherwise use request body
      return axios.post(`${API}/events/${id}/mark-ceased`, {
        ceased_date: ceasedDate,
        description,
        attachments
      });
    },
    // Enhanced mark ceased with reason and attachments
    markCeasedWithDetails: (id, data) => axios.post(`${API}/events/${id}/mark-ceased`, data),
    // Get comprehensive event timeline
    getTimeline: (id) => axios.get(`${API}/events/${id}/timeline`),
    toggleProgrammeImpact: (id, stillImpacting, extraData = null) => 
      axios.post(`${API}/events/${id}/toggle-programme-impact`, { 
        still_impacting_programme: stillImpacting, 
        programme_impact_notes: extraData?.reason || null,
        programme_impact_ended_date: extraData?.date || null,
        impact_reason_category: extraData?.impact_reason_category || null,
        attachment_ids: extraData?.attachment_ids || null,
      }),
    downloadNoticeCalendar: (id) => `${API}/events/${id}/calendar/notice`,
    downloadClaimCalendar: (id) => `${API}/events/${id}/calendar/claim`,
    downloadDeterminationCalendar: (id) => `${API}/events/${id}/calendar/determination`,
    sendReminder: (id) => axios.post(`${API}/events/${id}/send-reminder`),
    uploadDocument: (id, formData) => axios.post(`${API}/events/${id}/upload-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    // Interim claims (FIDIC)
    getInterimClaims: (eventId) => axios.get(`${API}/events/${eventId}/interim-claims`),
    submitInterimClaim: (eventId, data) => axios.post(`${API}/events/${eventId}/interim-claims`, data),
  },
  notices: {
    getById: (id) => axios.get(`${API}/notices/${id}`),
    update: (id, data) => axios.put(`${API}/notices/${id}`, data),
    delete: (id) => axios.delete(`${API}/notices/${id}`),
    share: (id, data) => axios.post(`${API}/notices/${id}/share`, data),
    convertToClaim: (id, isProspective = false) => axios.post(`${API}/notices/${id}/convert-to-claim`, null, {
      params: { is_prospective: isProspective },
      timeout: 120000 // 2 min timeout for AI generation
    }),
  },
  claims: {
    getById: (id) => axios.get(`${API}/claims/${id}`),
    update: (id, data) => axios.put(`${API}/claims/${id}`, data),
    delete: (id) => axios.delete(`${API}/claims/${id}`),
    share: (id, data) => axios.post(`${API}/claims/${id}/share`, data),
    // Readiness gate
    getReadiness: (id) => axios.get(`${API}/claims/${id}/readiness`),
    updateParticulars: (id, data) => axios.put(`${API}/claims/${id}/particulars`, data),
    gatedSubmit: (id) => axios.post(`${API}/claims/${id}/gated-submit`),
    // Supporting records
    getSupportingRecords: (id) => axios.get(`${API}/claims/${id}/supporting-records`),
    addSupportingRecord: (id, data) => axios.post(`${API}/claims/${id}/supporting-records`, data),
    updateSupportingRecord: (id, recordId, data) => axios.put(`${API}/claims/${id}/supporting-records/${recordId}`, data),
    deleteSupportingRecord: (id, recordId) => axios.delete(`${API}/claims/${id}/supporting-records/${recordId}`),
    // Overrides
    addOverride: (id, data) => axios.post(`${API}/claims/${id}/overrides`, data),
    removeOverride: (id, itemCode) => axios.delete(`${API}/claims/${id}/overrides/${itemCode}`),
    // Audit
    getAuditLog: (id) => axios.get(`${API}/claims/${id}/audit-log`),
  },
  users: {
    // CRUD
    getAll: (params = {}) => axios.get(`${API}/users`, { params }),
    getById: (id) => axios.get(`${API}/users/${id}`),
    create: (data) => axios.post(`${API}/users`, data),
    update: (id, data) => axios.put(`${API}/users/${id}`, data),
    delete: (id) => axios.delete(`${API}/users/${id}`),
    toggleActive: (id) => axios.post(`${API}/users/${id}/toggle-active`),
    regeneratePassword: (id, sendEmail = true) => axios.post(`${API}/users/${id}/regenerate-password?send_email=${sendEmail}`),
    // Permissions
    updatePermissions: (id, permissions) => axios.put(`${API}/users/${id}/permissions`, permissions),
    updateProjects: (id, projectIds) => axios.put(`${API}/users/${id}/projects`, projectIds),
    // Invitations
    invite: (data) => axios.post(`${API}/users/invite`, data),
    // Password management
    changePassword: (data) => axios.post(`${API}/users/change-password`, data),
    forgotPassword: (email) => axios.post(`${API}/users/forgot-password`, null, { params: { email } }),
    resetPassword: (data) => axios.post(`${API}/users/reset-password`, data),
    // Current user
    getMe: () => axios.get(`${API}/me`),
    updateMe: (data) => axios.put(`${API}/me`, data),
    // Roles
    getRoles: () => axios.get(`${API}/roles`),
    getRolesCategorized: () => axios.get(`${API}/roles/categorized`),
    getRole: (name) => axios.get(`${API}/roles/${name}`),
    createRole: (data) => axios.post(`${API}/roles`, data),
    updateRole: (name, data) => axios.put(`${API}/roles/${name}`, data),
    deleteRole: (name) => axios.delete(`${API}/roles/${name}`),
    deleteRoleOverride: (name) => axios.delete(`${API}/roles/${name}/override`),
  },
  userRequests: {
    create: (data) => axios.post(`${API}/user-requests`, data),
    getAll: () => axios.get(`${API}/user-requests`),
    approve: (id) => axios.post(`${API}/user-requests/${id}/approve`),
    reject: (id, reason) => axios.post(`${API}/user-requests/${id}/reject`, null, { params: { reason } }),
  },
  jbcc: {
    getNoticeClauses: () => axios.get(`${API}/jbcc/clauses/notice`),
    getClaimClauses: () => axios.get(`${API}/jbcc/clauses/claim`),
  },
  contracts: {
    getTypes: () => axios.get(`${API}/contracts/types`),
    getClauses: (contractType, context) => axios.get(`${API}/contracts/${encodeURIComponent(contractType)}/clauses/${context}`),
    getRules: (contractType) => axios.get(`${API}/contracts/${encodeURIComponent(contractType)}/rules`),
    getAllClauses: (contractType) => axios.get(`${API}/contracts/${encodeURIComponent(contractType)}/all-clauses`),
    getEffectiveClauses: (projectId, context) => axios.get(`${API}/projects/${projectId}/effective-clauses/${context}`),
  },
  // Delay Monitoring - Active delay tracking and cease workflow
  delayMonitoring: {
    // Summary and dashboard
    getSummary: (projectId) => axios.get(`${API}/projects/${projectId}/delay-summary`),
    
    // Cease workflow
    markCeased: (eventId, data) => axios.post(`${API}/events/${eventId}/submit-cease`, data),
    submitCease: (eventId, data) => axios.post(`${API}/events/${eventId}/submit-cease`, data),
    confirmCease: (eventId, data) => axios.post(`${API}/events/${eventId}/confirm-cease`, data),
    rejectCease: (eventId, reason) => axios.post(`${API}/events/${eventId}/reject-cease`, null, { params: { reason } }),
    
    // Weekly updates / status updates
    addUpdate: (eventId, data) => axios.post(`${API}/events/${eventId}/weekly-update`, data),
    addWeeklyUpdate: (eventId, data) => axios.post(`${API}/events/${eventId}/weekly-update`, data),
    
    // Timeline
    getTimeline: (eventId) => axios.get(`${API}/events/${eventId}/timeline`),
    
    // Email action tokens (no auth required)
    getActionDetails: (token) => axios.get(`${API}/delay-action/${token}`),
    generateActionToken: (eventId, action) => axios.post(`${API}/events/${eventId}/generate-action-token`, null, { params: { action } }),
    
    // Project monitoring settings
    getSettings: (projectId) => axios.get(`${API}/projects/${projectId}/monitoring-settings`),
    updateSettings: (projectId, data) => axios.put(`${API}/projects/${projectId}/monitoring-settings`, data),
  },
  // System SMTP API methods
  smtp: {
    getSettings: () => axios.get(`${API}/smtp-settings`),
    getSettingsByClient: (clientId) => axios.get(`${API}/smtp-settings/${clientId}`),
    createSettings: (data) => axios.post(`${API}/smtp-settings`, data),
    createSettingsForClient: (clientId, data) => axios.post(`${API}/smtp-settings/${clientId}`, data),
    deleteSettings: (clientId) => axios.delete(`${API}/smtp-settings/${clientId}`),
    testConnection: () => axios.post(`${API}/smtp-settings/test`),
    // System-wide SMTP settings
    getSystemSettings: () => axios.get(`${API}/smtp-settings/system`),
    createSystemSettings: (data) => axios.post(`${API}/smtp-settings/system`, data),
    deleteSystemSettings: () => axios.delete(`${API}/smtp-settings/system`),
    testSystemEmail: (email) => axios.post(`${API}/smtp-settings/system/test`, null, { params: { test_email: email } }),
  },
  // RFI Management
  rfi: {
    // CRUD
    getByProject: (projectId, params = {}) => axios.get(`${API}/projects/${projectId}/rfis`, { params }),
    getById: (projectId, rfiId) => axios.get(`${API}/projects/${projectId}/rfis/${rfiId}`),
    create: (projectId, data) => axios.post(`${API}/projects/${projectId}/rfis`, data),
    update: (projectId, rfiId, data) => axios.put(`${API}/projects/${projectId}/rfis/${rfiId}`, data),
    delete: (projectId, rfiId) => axios.delete(`${API}/projects/${projectId}/rfis/${rfiId}`),
    // Attachments
    uploadAttachment: (projectId, rfiId, formData) => 
      axios.post(`${API}/projects/${projectId}/rfis/${rfiId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    deleteAttachment: (projectId, rfiId, attachmentId) => 
      axios.delete(`${API}/projects/${projectId}/rfis/${rfiId}/attachments/${attachmentId}`),
    // Actions
    rewriteQuery: (projectId, rfiId) => axios.post(`${API}/projects/${projectId}/rfis/${rfiId}/rewrite`),
    issue: (projectId, rfiId, options = {}) => axios.post(`${API}/projects/${projectId}/rfis/${rfiId}/issue`, null, {
      params: {
        send_type: options.send_type || 'external',
        send_email: options.send_email !== false
      }
    }),
    respond: (projectId, rfiId, data) => axios.post(`${API}/projects/${projectId}/rfis/${rfiId}/respond`, data),
    close: (projectId, rfiId, data) => axios.post(`${API}/projects/${projectId}/rfis/${rfiId}/close`, data),
    generateNarrative: (projectId, rfiId) => axios.post(`${API}/projects/${projectId}/rfis/${rfiId}/generate-narrative`, null, { timeout: 120000 }),
    getTimeline: (projectId, rfiId) => axios.get(`${API}/projects/${projectId}/rfis/${rfiId}/timeline`),
    // Reference data
    getDisciplines: () => axios.get(`${API}/rfi/disciplines`),
    getPriorities: () => axios.get(`${API}/rfi/priorities`),
    getStatuses: () => axios.get(`${API}/rfi/statuses`),
    getClosureEffects: () => axios.get(`${API}/rfi/closure-effects`),
  },
  // Site Instructions (SI) Management
  siteInstructions: {
    // CRUD
    getByProject: (projectId, params = {}) => axios.get(`${API}/projects/${projectId}/site-instructions`, { params }),
    getById: (siId) => axios.get(`${API}/site-instructions/${siId}`),
    create: (projectId, data) => axios.post(`${API}/projects/${projectId}/site-instructions`, data),
    update: (siId, data) => axios.put(`${API}/site-instructions/${siId}`, data),
    delete: (siId) => axios.delete(`${API}/site-instructions/${siId}`),
    // AI Extraction
    extractFromDocument: (fileBase64, fileType, mimeType = 'image/jpeg') => 
      axios.post(`${API}/site-instructions/extract`, {
        file_base64: fileBase64,
        file_type: fileType,
        mime_type: mimeType
      }),
    extractAndCreate: (projectId, formData) => 
      axios.post(`${API}/site-instructions/extract-and-create?project_id=${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    // Attachments
    uploadAttachment: (siId, formData, tag = 'Other') => {
      formData.append('tag', tag);
      return axios.post(`${API}/site-instructions/${siId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    deleteAttachment: (siId, attachmentId) => 
      axios.delete(`${API}/site-instructions/${siId}/attachments/${attachmentId}`),
    // Correspondence
    getCorrespondence: (siId) => axios.get(`${API}/site-instructions/${siId}/correspondence`),
    addCorrespondence: (siId, data) => axios.post(`${API}/site-instructions/${siId}/correspondence`, data),
    // Internal Comments
    getInternalComments: (siId) => axios.get(`${API}/site-instructions/${siId}/internal-comments`),
    addInternalComment: (siId, data) => axios.post(`${API}/site-instructions/${siId}/internal-comments`, data),
    deleteInternalComment: (siId, commentId) => 
      axios.delete(`${API}/site-instructions/${siId}/internal-comments/${commentId}`),
    // Clarifications
    getClarifications: (siId) => axios.get(`${API}/site-instructions/${siId}/clarifications`),
    addClarification: (siId, data) => axios.post(`${API}/site-instructions/${siId}/clarifications`, data),
    getClarificationRequests: (siId) => axios.get(`${API}/site-instructions/${siId}/clarification-requests`),
    // Actions
    distributeInternal: (siId, recipients) => 
      axios.post(`${API}/site-instructions/${siId}/distribute-internal`, recipients),
    sendForClarification: (siId, questions) => 
      axios.post(`${API}/site-instructions/${siId}/send-for-clarification`, null, { params: { questions } }),
    close: (siId, closureNotes) => 
      axios.post(`${API}/site-instructions/${siId}/close`, null, { params: { closure_notes: closureNotes } }),
    convertToRfi: (siId, rfiData) => 
      axios.post(`${API}/site-instructions/${siId}/convert-to-rfi`, rfiData),
    convertToNotice: (siId, noticeData) => 
      axios.post(`${API}/site-instructions/${siId}/convert-to-notice`, noticeData),
    convertToDelayEvent: (siId, eventData) => 
      axios.post(`${API}/site-instructions/${siId}/convert-to-delay-event`, eventData),
    // Consolidation
    consolidateComments: (siId) => 
      axios.post(`${API}/site-instructions/${siId}/consolidate-comments`),
    getConsolidation: (siId) => 
      axios.get(`${API}/site-instructions/${siId}/consolidation`),
    getDistributionTokens: (siId) => 
      axios.get(`${API}/site-instructions/${siId}/distribution-tokens`),
    getAgingDetails: (siId) => axios.get(`${API}/site-instructions/${siId}/aging`),
    sendReminder: (siId) => axios.post(`${API}/site-instructions/${siId}/send-reminder`),
    // Workflow Actions
    submitForApproval: (siId, data) => axios.post(`${API}/site-instructions/${siId}/submit-for-approval`, data),
    approve: (siId, data = {}) => axios.post(`${API}/site-instructions/${siId}/approve`, data),
    reject: (siId, data) => axios.post(`${API}/site-instructions/${siId}/reject`, data),
    void: (siId, data) => axios.post(`${API}/site-instructions/${siId}/void`, data),
    editForApproval: (siId, data) => axios.put(`${API}/site-instructions/${siId}/edit-for-approval`, data),
  },
  // SI Dashboard & Aging Stats
  siDashboard: {
    getProjectAgingStats: (projectId) => axios.get(`${API}/projects/${projectId}/si-aging-stats`),
    getSummary: () => axios.get(`${API}/dashboard/si-summary`),
    getOverdueClarifications: (projectId) => axios.get(`${API}/projects/${projectId}/si-overdue-clarifications`),
    sendOverdueSummary: (projectId) => axios.post(`${API}/projects/${projectId}/send-overdue-summary`),
  },
  // RFI Dashboard Summary
  rfiDashboard: {
    getSummary: () => axios.get(`${API}/dashboard/rfi-summary`),
  },
  // Company Settings
  companySettings: {
    get: () => axios.get(`${API}/company-settings`),
    save: (data) => axios.post(`${API}/company-settings`, data),
    uploadLogo: (formData) => axios.post(`${API}/company-settings/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getDocumentHeader: () => axios.get(`${API}/company-settings/document-header`),
  },
  // Baseline Programs (Enhanced)
  programs: {
    // Reference data
    getTypes: () => axios.get(`${API}/program-types`),
    getFormats: () => axios.get(`${API}/program-formats`),
    getStatuses: () => axios.get(`${API}/program-statuses`),
    // Program CRUD
    list: (projectId, programType = null) => 
      axios.get(`${API}/projects/${projectId}/programs`, { params: { program_type: programType } }),
    get: (projectId, programId, includeActivities = false) => 
      axios.get(`${API}/projects/${projectId}/programs/${programId}`, { params: { include_activities: includeActivities } }),
    create: (projectId, data) => 
      axios.post(`${API}/projects/${projectId}/programs`, data),
    upload: (projectId, formData) => 
      axios.post(`${API}/projects/${projectId}/programs/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    update: (projectId, programId, data) => 
      axios.put(`${API}/projects/${projectId}/programs/${programId}`, data),
    analyze: (projectId, programId) => 
      axios.post(`${API}/projects/${projectId}/programs/${programId}/analyze`),
    approve: (projectId, programId) => 
      axios.post(`${API}/projects/${projectId}/programs/${programId}/approve`),
    approveBaseline: (projectId, programId) => 
      axios.post(`${API}/projects/${projectId}/programs/${programId}/approve-baseline`),
    uploadNative: (projectId, programId, formData) =>
      axios.post(`${API}/projects/${projectId}/programs/${programId}/upload-native`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    uploadData: (projectId, programId, formData) =>
      axios.post(`${API}/projects/${projectId}/programs/${programId}/upload-data`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    submitUpdate: (projectId, programId, data) =>
      axios.post(`${API}/projects/${projectId}/programs/${programId}/submit-update`, data),
    delete: (projectId, programId) => 
      axios.delete(`${API}/projects/${projectId}/programs/${programId}`),
    // Comparison
    compare: (projectId, baseProgramId, compareProgramId) =>
      axios.post(`${API}/projects/${projectId}/programs/compare`, null, {
        params: { base_program_id: baseProgramId, compare_program_id: compareProgramId }
      }),
    getComparisons: (projectId) => 
      axios.get(`${API}/projects/${projectId}/comparisons`),
    getComparison: (projectId, comparisonId) => 
      axios.get(`${API}/projects/${projectId}/comparisons/${comparisonId}`),
    commentOnDelay: (projectId, comparisonId, delayId, data) =>
      axios.put(`${API}/projects/${projectId}/comparisons/${comparisonId}/delays/${delayId}/comment`, null, {
        params: data
      }),
    convertDelayToEvent: (projectId, comparisonId, delayId) =>
      axios.post(`${API}/projects/${projectId}/comparisons/${comparisonId}/delays/${delayId}/convert-to-event`),
    // Program Requests
    createRequest: (projectId, data) => 
      axios.post(`${API}/projects/${projectId}/program-requests`, data),
    getRequests: (projectId) => 
      axios.get(`${API}/projects/${projectId}/program-requests`),
  },
  // Sub-Contractors
  subcontractors: {
    getTrades: () => axios.get(`${API}/subcontractor-trades`),
    getAll: (projectId) => axios.get(`${API}/projects/${projectId}/subcontractors`),
    get: (projectId, subcontractorId) => 
      axios.get(`${API}/projects/${projectId}/subcontractors/${subcontractorId}`),
    create: (projectId, data) => 
      axios.post(`${API}/projects/${projectId}/subcontractors`, data),
    update: (projectId, subcontractorId, data) => 
      axios.put(`${API}/projects/${projectId}/subcontractors/${subcontractorId}`, data),
    delete: (projectId, subcontractorId) => 
      axios.delete(`${API}/projects/${projectId}/subcontractors/${subcontractorId}`),
    getPrograms: (projectId, subcontractorId) =>
      axios.get(`${API}/projects/${projectId}/subcontractors/${subcontractorId}/programs`),
  },
  // Contract Configuration
  contractConfig: {
    get: (projectId) => axios.get(`${API}/projects/${projectId}/contract-config`),
    save: (projectId, data) => axios.post(`${API}/projects/${projectId}/contract-config`, data),
  },
  // Public SI Comment (no auth required)
  publicSIComment: {
    getSIDetails: (tokenId) => axios.get(`${API}/public/si-comment/${tokenId}`),
    submitComment: (tokenId, data) => axios.post(`${API}/public/si-comment/${tokenId}`, data),
  },
  // Public SI Clarification (no auth required - for consultants)
  publicSIClarification: {
    getSIDetails: (tokenId) => axios.get(`${API}/public/si-clarification/${tokenId}`),
    submitClarification: (tokenId, data) => axios.post(`${API}/public/si-clarification/${tokenId}`, data),
  },
  // Drawings Module
  drawings: {
    // Reference data
    getDisciplines: () => axios.get(`${API}/drawings/disciplines`),
    getIssuePurposes: () => axios.get(`${API}/drawings/issue-purposes`),
    getStatuses: () => axios.get(`${API}/drawings/statuses`),
    // Drawing CRUD
    getByProject: (projectId, params = {}) => 
      axios.get(`${API}/projects/${projectId}/drawings`, { params }),
    get: (projectId, drawingId) => 
      axios.get(`${API}/projects/${projectId}/drawings/${drawingId}`),
    create: (projectId, data) => 
      axios.post(`${API}/projects/${projectId}/drawings`, data),
    update: (projectId, drawingId, data) => 
      axios.put(`${API}/projects/${projectId}/drawings/${drawingId}`, data),
    delete: (projectId, drawingId) => 
      axios.delete(`${API}/projects/${projectId}/drawings/${drawingId}`),
    // File upload
    uploadPdf: (projectId, formData, extractMetadata = true) => {
      formData.append('extract_metadata', extractMetadata);
      return axios.post(`${API}/projects/${projectId}/drawings/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    // AI Extraction
    extractMetadata: (fileBase64, fileType = 'pdf', mimeType = 'application/pdf') => {
      const formData = new FormData();
      formData.append('file_base64', fileBase64);
      formData.append('file_type', fileType);
      formData.append('mime_type', mimeType);
      return axios.post(`${API}/drawings/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
  },
  // Drawing Revisions
  drawingRevisions: {
    getAll: (projectId, drawingId) => 
      axios.get(`${API}/projects/${projectId}/drawings/${drawingId}/revisions`),
    get: (revisionId) => 
      axios.get(`${API}/drawing-revisions/${revisionId}`),
    create: (projectId, drawingId, data) => 
      axios.post(`${API}/projects/${projectId}/drawings/${drawingId}/revisions`, data),
    update: (revisionId, data) => 
      axios.put(`${API}/drawing-revisions/${revisionId}`, data),
    // Diff generation
    generateDiff: (revisionId) => 
      axios.post(`${API}/drawing-revisions/${revisionId}/generate-diff`),
    // Distribution
    distribute: (revisionId, recipients) => 
      axios.post(`${API}/drawing-revisions/${revisionId}/distribute`, recipients),
    acknowledge: (revisionId) => 
      axios.post(`${API}/drawing-revisions/${revisionId}/acknowledge`),
    // Workflow actions
    createRfi: (revisionId, rfiData) => 
      axios.post(`${API}/drawing-revisions/${revisionId}/create-rfi`, rfiData),
    convertToDelayEvent: (revisionId, eventData) => 
      axios.post(`${API}/drawing-revisions/${revisionId}/convert-to-delay-event`, eventData),
    // Queries
    addQuery: (revisionId, queryData) => 
      axios.post(`${API}/drawing-revisions/${revisionId}/queries`, queryData),
    respondToQuery: (revisionId, queryId, responseData) => 
      axios.put(`${API}/drawing-revisions/${revisionId}/queries/${queryId}/respond`, responseData),
    // Correspondence
    addCorrespondence: (revisionId, corrData) => 
      axios.post(`${API}/drawing-revisions/${revisionId}/correspondence`, corrData),
    // Timeline
    getTimeline: (revisionId) => 
      axios.get(`${API}/drawing-revisions/${revisionId}/timeline`),
  },
  // Drawing Dashboard
  drawingsDashboard: {
    getSummary: () => axios.get(`${API}/dashboard/drawings-summary`),
  },
  // Teams aliases for Drawing components compatibility
  teams: {
    getProfessional: (projectId) => axios.get(`${API}/projects/${projectId}/professional-team`),
    getInternal: (projectId) => axios.get(`${API}/projects/${projectId}/internal-team`),
  },
  // Project Summary Dashboard
  dashboard: {
    getSummary: (projectId, days = 7) => axios.get(`${API}/dashboard/projects/${projectId}/summary?days=${days}`),
    getRecentActivity: (projectId, days = 7) => axios.get(`${API}/dashboard/projects/${projectId}/recent-activity?days=${days}`),
    getUrgentDeadlines: (projectId) => axios.get(`${API}/dashboard/projects/${projectId}/urgent-deadlines`),
    getCalendarItems: (projectId, startDate, endDate) => axios.get(`${API}/dashboard/projects/${projectId}/calendar-items`, {
      params: { start_date: startDate, end_date: endDate }
    }),
    getMonthlySummary: (projectId, year, month) => axios.get(`${API}/dashboard/projects/${projectId}/monthly-summary`, {
      params: { year, month }
    }),
    generateAISummary: (projectId, days = 7) => axios.post(`${API}/dashboard/projects/${projectId}/generate-summary?days=${days}`),
  },
  // Tasks Module
  tasks: {
    getByProject: (projectId, params = {}) => axios.get(`${API}/tasks/projects/${projectId}`, { params }),
    get: (projectId, taskId) => axios.get(`${API}/tasks/projects/${projectId}/${taskId}`),
    create: (projectId, data) => axios.post(`${API}/tasks/projects/${projectId}`, data),
    update: (projectId, taskId, data) => axios.put(`${API}/tasks/projects/${projectId}/${taskId}`, data),
    delete: (projectId, taskId) => axios.delete(`${API}/tasks/projects/${projectId}/${taskId}`),
    addNote: (projectId, taskId, text) => axios.post(`${API}/tasks/projects/${projectId}/${taskId}/notes?note_text=${encodeURIComponent(text)}`),
    getMyTasks: (params = {}) => axios.get(`${API}/tasks/my-tasks`, { params }),
    migrateActions: () => axios.post(`${API}/tasks/migrate-meeting-actions`),
  },
  // CPM Scheduling Module
  cpm: {
    // Schedule CRUD
    getSchedule: (projectId) => axios.get(`${API}/cpm/projects/${projectId}/schedule`),
    createSchedule: (projectId, data) => axios.post(`${API}/cpm/projects/${projectId}/schedule`, data),
    updateSchedule: (projectId, data) => axios.put(`${API}/cpm/projects/${projectId}/schedule`, data),
    // Activities
    getActivities: (projectId, wbsId = null) => axios.get(`${API}/cpm/projects/${projectId}/activities`, { 
      params: wbsId ? { wbs_id: wbsId } : {} 
    }),
    createActivity: (projectId, data) => axios.post(`${API}/cpm/projects/${projectId}/activities`, data),
    updateActivity: (projectId, activityId, data) => axios.put(`${API}/cpm/projects/${projectId}/activities/${activityId}`, data),
    deleteActivity: (projectId, activityId) => axios.delete(`${API}/cpm/projects/${projectId}/activities/${activityId}`),
    // Relationships
    getRelationships: (projectId) => axios.get(`${API}/cpm/projects/${projectId}/relationships`),
    createRelationship: (projectId, data) => axios.post(`${API}/cpm/projects/${projectId}/relationships`, data),
    deleteRelationship: (projectId, relationshipId) => axios.delete(`${API}/cpm/projects/${projectId}/relationships/${relationshipId}`),
    // Calendars
    getCalendars: (projectId) => axios.get(`${API}/cpm/projects/${projectId}/calendars`),
    createCalendar: (projectId, data) => axios.post(`${API}/cpm/projects/${projectId}/calendars`, data),
    updateCalendar: (projectId, calendarId, data) => axios.put(`${API}/cpm/projects/${projectId}/calendars/${calendarId}`, data),
    // WBS
    getWBS: (projectId) => axios.get(`${API}/cpm/projects/${projectId}/wbs`),
    createWBSNode: (projectId, data) => axios.post(`${API}/cpm/projects/${projectId}/wbs`, data),
    // Baselines
    getBaselines: (projectId) => axios.get(`${API}/cpm/projects/${projectId}/baselines`),
    createBaseline: (projectId, data) => axios.post(`${API}/cpm/projects/${projectId}/baselines`, data),
    // Compute
    compute: (projectId) => axios.post(`${API}/cpm/projects/${projectId}/compute`),
    // Quality Check
    qualityCheck: (projectId) => axios.get(`${API}/cpm/projects/${projectId}/quality-check`),
  },
  // CRM Module
  crm: {
    // Dashboard
    getDashboard: () => axios.get(`${API}/crm/dashboard`),
    // Companies
    getCompanies: (params = {}) => axios.get(`${API}/crm/companies`, { params }),
    getCompany: (id) => axios.get(`${API}/crm/companies/${id}`),
    createCompany: (data) => axios.post(`${API}/crm/companies`, data),
    updateCompany: (id, data) => axios.put(`${API}/crm/companies/${id}`, data),
    deleteCompany: (id) => axios.delete(`${API}/crm/companies/${id}`),
    // Contacts
    getContacts: (params = {}) => axios.get(`${API}/crm/contacts`, { params }),
    getContact: (id) => axios.get(`${API}/crm/contacts/${id}`),
    createContact: (data) => axios.post(`${API}/crm/contacts`, data),
    updateContact: (id, data) => axios.put(`${API}/crm/contacts/${id}`, data),
    deleteContact: (id) => axios.delete(`${API}/crm/contacts/${id}`),
    // Opportunities
    getOpportunities: (params = {}) => axios.get(`${API}/crm/opportunities`, { params }),
    getOpportunity: (id) => axios.get(`${API}/crm/opportunities/${id}`),
    createOpportunity: (data) => axios.post(`${API}/crm/opportunities`, data),
    updateOpportunity: (id, data) => axios.put(`${API}/crm/opportunities/${id}`, data),
    deleteOpportunity: (id) => axios.delete(`${API}/crm/opportunities/${id}`),
    // Activities
    getActivities: (params = {}) => axios.get(`${API}/crm/activities`, { params }),
    createActivity: (data) => axios.post(`${API}/crm/activities`, data),
    // Tasks
    getTasks: (params = {}) => axios.get(`${API}/crm/tasks`, { params }),
    createTask: (data) => axios.post(`${API}/crm/tasks`, data),
    updateTask: (id, data) => axios.put(`${API}/crm/tasks/${id}`, data),
    deleteTask: (id) => axios.delete(`${API}/crm/tasks/${id}`),
    // Quotes
    getQuotes: (params = {}) => axios.get(`${API}/crm/quotes`, { params }),
    getQuote: (id) => axios.get(`${API}/crm/quotes/${id}`),
    createQuote: (data) => axios.post(`${API}/crm/quotes`, data),
    updateQuote: (id, data) => axios.put(`${API}/crm/quotes/${id}`, data),
    deleteQuote: (id) => axios.delete(`${API}/crm/quotes/${id}`),
    downloadQuotePdf: (id) => axios.get(`${API}/crm/quotes/${id}/pdf`, { responseType: 'blob' }),
    sendQuote: (id, data) => axios.post(`${API}/crm/quotes/${id}/send`, data),
  },
  // Reports
  reports: {
    generate: (data) => axios.post(`${API}/reports/generate`, data),
    preview: (params) => axios.get(`${API}/reports/preview`, { params }),
    getAvailablePeriods: (projectId) => axios.get(`${API}/reports/available-periods`, { params: { project_id: projectId } }),
    getVisibilityOptions: () => axios.get(`${API}/reports/visibility-options`),
  },
  // Invitations
  invitations: {
    invite: (data) => axios.post(`${API}/invitations/invite`, data),
    verify: (token) => axios.get(`${API}/invitations/verify/${token}`),
    accept: (data) => axios.post(`${API}/invitations/accept`, data),
    resend: (userId) => axios.post(`${API}/invitations/resend/${userId}`),
    getLink: (userId) => axios.get(`${API}/invitations/link/${userId}`),
    bulkInvite: (data) => axios.post(`${API}/invitations/bulk-invite`, data),
    getPending: () => axios.get(`${API}/invitations/pending`),
    checkReminders: () => axios.post(`${API}/invitations/check-reminders`),
  },
  // Admin User Management
  adminUsers: {
    getAll: (params) => axios.get(`${API}/admin/users`, { params }),
    getGroupedByProject: (params) => axios.get(`${API}/admin/users/grouped-by-project`, { params }),
    getStats: () => axios.get(`${API}/admin/users/stats`),
    getById: (id) => axios.get(`${API}/admin/users/${id}`),
    update: (id, data) => axios.put(`${API}/admin/users/${id}`, data),
    disable: (id) => axios.post(`${API}/admin/users/${id}/disable`),
    enable: (id) => axios.post(`${API}/admin/users/${id}/enable`),
    delete: (id) => axios.delete(`${API}/admin/users/${id}`),
    bulkAction: (data) => axios.post(`${API}/admin/users/bulk-action`, data),
    bulkAssignProjects: (data) => axios.post(`${API}/admin/users/bulk-assign-projects`, data),
    resetPassword: (id) => axios.post(`${API}/admin/users/${id}/reset-password`),
  },
  // Team Member Requests
  teamRequests: {
    create: (data) => axios.post(`${API}/team-requests`, data),
    getAll: (params) => axios.get(`${API}/team-requests`, { params }),
    getPendingCount: () => axios.get(`${API}/team-requests/pending-count`),
    getById: (id) => axios.get(`${API}/team-requests/${id}`),
    approve: (id, data) => axios.post(`${API}/team-requests/${id}/approve`, data || {}),
    reject: (id, data) => axios.post(`${API}/team-requests/${id}/reject`, data),
    delete: (id) => axios.delete(`${API}/team-requests/${id}`),
  },
  // Feedback & Release Management
  feedback: {
    // User endpoints
    reportBug: (data) => axios.post(`${API}/feedback/tickets/bug`, data),
    requestFeature: (data) => axios.post(`${API}/feedback/tickets/feature`, data),
    uploadAttachment: (ticketId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/feedback/tickets/${ticketId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    getMyTickets: (params) => axios.get(`${API}/feedback/tickets/my`, { params }),
    getTicket: (id) => axios.get(`${API}/feedback/tickets/${id}`),
    vote: (ticketId) => axios.post(`${API}/feedback/tickets/${ticketId}/vote`),
    unvote: (ticketId) => axios.delete(`${API}/feedback/tickets/${ticketId}/vote`),
    addComment: (ticketId, data) => axios.post(`${API}/feedback/tickets/${ticketId}/comments`, data),
    getRoadmap: () => axios.get(`${API}/feedback/roadmap`),
    // Admin endpoints
    adminGetTickets: (params) => axios.get(`${API}/feedback/admin/tickets`, { params }),
    adminUpdateTicket: (id, data) => axios.put(`${API}/feedback/admin/tickets/${id}`, data),
    adminGetActivity: (ticketId) => axios.get(`${API}/feedback/admin/tickets/${ticketId}/activity`),
    adminGetStats: () => axios.get(`${API}/feedback/admin/stats`),
    // Announcements
    createAnnouncement: (data) => axios.post(`${API}/feedback/admin/announcements`, data),
    getAnnouncements: (params) => axios.get(`${API}/feedback/admin/announcements`, { params }),
    previewAnnouncement: (id) => axios.get(`${API}/feedback/admin/announcements/${id}/preview`),
    sendAnnouncement: (id) => axios.post(`${API}/feedback/admin/announcements/${id}/send`),
  },
  // File Upload
  upload: {
    single: (formData) => axios.post(`${API}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    multiple: (formData) => axios.post(`${API}/upload/multiple`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  },
  // User Settings & Notifications
  userSettings: {
    getProfile: () => axios.get(`${API}/user-settings/profile`),
    updateProfile: (data) => axios.put(`${API}/user-settings/profile`, data),
    getNotifications: () => axios.get(`${API}/user-settings/notifications`),
    updateNotifications: (data) => axios.put(`${API}/user-settings/notifications`, data),
    sendTestDigest: () => axios.post(`${API}/user-settings/digest/test`, {}),
    previewDigest: () => axios.get(`${API}/user-settings/digest/preview`),
  },
  // Document Control Register
  documentControl: {
    getFamilies: () => axios.get(`${API}/contract-templates/families`),
    getForms: (family) => axios.get(`${API}/contract-templates/${family}/forms`),
    getTerminology: (family) => axios.get(`${API}/contract-templates/${family}/terminology`),
    getSetupFields: (family) => axios.get(`${API}/contract-templates/${family}/setup-fields`),
    initialize: (projectId) => axios.post(`${API}/projects/${projectId}/document-register/initialize`),
    getRegister: (projectId, params) => axios.get(`${API}/projects/${projectId}/document-register`, { params }),
    addRecord: (projectId, data) => axios.post(`${API}/projects/${projectId}/document-register`, data),
    getRecord: (projectId, recordId) => axios.get(`${API}/projects/${projectId}/document-register/${recordId}`),
    updateRecord: (projectId, recordId, data) => axios.put(`${API}/projects/${projectId}/document-register/${recordId}`, data),
    deleteRecord: (projectId, recordId) => axios.delete(`${API}/projects/${projectId}/document-register/${recordId}`),
    verifyRecord: (projectId, recordId) => axios.post(`${API}/projects/${projectId}/document-register/${recordId}/verify`),
    rejectRecord: (projectId, recordId, reason) => axios.post(`${API}/projects/${projectId}/document-register/${recordId}/reject?reason=${encodeURIComponent(reason)}`),
    markNA: (projectId, recordId) => axios.post(`${API}/projects/${projectId}/document-register/${recordId}/mark-na`),
    getAccessRecords: (projectId) => axios.get(`${API}/projects/${projectId}/access-records`),
    addAccessRecord: (projectId, data) => axios.post(`${API}/projects/${projectId}/access-records`, data),
    updateAccessRecord: (projectId, recordId, data) => axios.put(`${API}/projects/${projectId}/access-records/${recordId}`, data),
    deleteAccessRecord: (projectId, recordId) => axios.delete(`${API}/projects/${projectId}/access-records/${recordId}`),
    getReadiness: (projectId) => axios.get(`${API}/projects/${projectId}/setup-readiness`),
    markLive: (projectId) => axios.post(`${API}/projects/${projectId}/mark-live`),
    getAuditLog: (projectId) => axios.get(`${API}/projects/${projectId}/setup-audit-log`),
    getProjectTerminology: (projectId) => axios.get(`${API}/projects/${projectId}/terminology`),
  },
  // File Storage (Object Storage)
  files: {
    upload: (projectId, file, category, documentRecordId) => {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams({ category });
      if (documentRecordId) params.append('document_record_id', documentRecordId);
      return axios.post(`${API}/projects/${projectId}/files/upload?${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
    },
    download: (fileId) => axios.get(`${API}/files/${fileId}/download`, { responseType: 'blob' }),
    list: (projectId, params) => axios.get(`${API}/projects/${projectId}/files`, { params }),
    delete: (fileId) => axios.delete(`${API}/files/${fileId}`),
    extractContract: (projectId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/projects/${projectId}/contracts/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
    },
  },
  // Close-Out Engine
  closeout: {
    // Contract Profiles
    getProfiles: () => axios.get(`${API}/closeout/contract-profiles`),
    getProfile: (id) => axios.get(`${API}/closeout/contract-profiles/${id}`),
    createProfile: (data) => axios.post(`${API}/closeout/contract-profiles`, data),
    updateProfile: (id, data) => axios.put(`${API}/closeout/contract-profiles/${id}`, data),
    getStageLabels: (family) => axios.get(`${API}/closeout/stage-labels/${family}`),
    getDefectStatuses: () => axios.get(`${API}/closeout/defect-statuses`),
    getTradeCodes: () => axios.get(`${API}/closeout/trade-codes`),
    // Project Settings
    getProjectSettings: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/settings`),
    updateProjectSettings: (projectId, data) => axios.put(`${API}/closeout/projects/${projectId}/settings`, data),
    // Dashboard
    getDashboard: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/dashboard`),
    // Packages
    listPackages: (projectId, params) => axios.get(`${API}/closeout/projects/${projectId}/packages`, { params }),
    createPackage: (projectId, data) => axios.post(`${API}/closeout/projects/${projectId}/packages`, data),
    getPackage: (id) => axios.get(`${API}/closeout/packages/${id}`),
    updatePackage: (id, data) => axios.put(`${API}/closeout/packages/${id}`, data),
    deletePackage: (id) => axios.delete(`${API}/closeout/packages/${id}`),
    getPackageHistory: (id) => axios.get(`${API}/closeout/packages/${id}/stage-history`),
    // Defects
    listDefects: (projectId, params) => axios.get(`${API}/closeout/projects/${projectId}/defects`, { params }),
    createDefect: (projectId, data) => axios.post(`${API}/closeout/projects/${projectId}/defects`, data),
    getDefect: (id) => axios.get(`${API}/closeout/defects/${id}`),
    updateDefect: (id, data) => axios.put(`${API}/closeout/defects/${id}`, data),
    deleteDefect: (id) => axios.delete(`${API}/closeout/defects/${id}`),
    addDefectComment: (id, text, type) => axios.post(`${API}/closeout/defects/${id}/comments?comment_text=${encodeURIComponent(text)}&comment_type=${type}`),
    addDefectPhoto: (id, file, photoType, caption) => {
      const fd = new FormData();
      fd.append('file', file);
      return axios.post(`${API}/closeout/defects/${id}/photos?photo_type=${photoType}${caption ? '&caption=' + encodeURIComponent(caption) : ''}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    addReinspection: (id, result, comments) => axios.post(`${API}/closeout/defects/${id}/reinspections?result=${result}${comments ? '&comments=' + encodeURIComponent(comments) : ''}`),
    // Consultant Uploads
    listUploads: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/consultant-uploads`),
    createUpload: (projectId, file, params) => {
      const fd = new FormData();
      fd.append('file', file);
      const qs = new URLSearchParams(params).toString();
      return axios.post(`${API}/closeout/projects/${projectId}/consultant-uploads?${qs}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
    },
    triggerExtraction: (uploadId) => axios.post(`${API}/closeout/consultant-uploads/${uploadId}/extract`, null, { timeout: 120000 }),
    getExtractedLines: (uploadId) => axios.get(`${API}/closeout/consultant-uploads/${uploadId}/extracted-lines`),
    updateExtractedLine: (lineId, params) => {
      const qs = new URLSearchParams(params).toString();
      return axios.put(`${API}/closeout/extracted-lines/${lineId}?${qs}`);
    },
    approveExtractedLines: (uploadId) => axios.post(`${API}/closeout/consultant-uploads/${uploadId}/approve`),
    // Task Allocation
    getTaskAllocation: (projectId, params) => axios.get(`${API}/closeout/projects/${projectId}/task-allocation`, { params }),
    bulkAssign: (projectId, data) => axios.post(`${API}/closeout/projects/${projectId}/bulk-assign`, data),
    // Documents
    listDocuments: (projectId, params) => axios.get(`${API}/closeout/projects/${projectId}/documents`, { params }),
    createDocument: (projectId, data) => axios.post(`${API}/closeout/projects/${projectId}/documents`, data),
    updateDocStatus: (docId, status, comments) => axios.put(`${API}/closeout/documents/${docId}/status?status=${status}${comments ? '&comments=' + encodeURIComponent(comments) : ''}`),
    submitDocument: (docId, data) => axios.post(`${API}/closeout/documents/${docId}/submissions`, data),
    getDocSubmissions: (docId) => axios.get(`${API}/closeout/documents/${docId}/submissions`),
    // Sign-offs
    listSignoffs: (projectId, params) => axios.get(`${API}/closeout/projects/${projectId}/signoffs`, { params }),
    createSignoff: (projectId, data) => axios.post(`${API}/closeout/projects/${projectId}/signoffs`, data),
    signoffAction: (signoffId, action, comments) => axios.put(`${API}/closeout/signoffs/${signoffId}/action?action=${action}${comments ? '&comments=' + encodeURIComponent(comments) : ''}`),
    // Assets
    listAssets: (projectId, params) => axios.get(`${API}/closeout/projects/${projectId}/assets`, { params }),
    createAsset: (projectId, data) => axios.post(`${API}/closeout/projects/${projectId}/assets`, data),
    updateAsset: (assetId, params) => { const qs = new URLSearchParams(params).toString(); return axios.put(`${API}/closeout/assets/${assetId}?${qs}`); },
    // Reports
    reportDefectRegister: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/reports/defect-register`),
    reportBySubcontractor: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/reports/by-subcontractor`),
    reportBlockers: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/reports/completion-blockers`),
    reportHandover: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/reports/handover-readiness`),
    reportUploads: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/reports/consultant-uploads`),
    reportOutstandingDocs: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/reports/outstanding-documents`),
    // Portal
    portalMyDefects: (company, projectId) => axios.get(`${API}/closeout/portal/my-defects?company=${encodeURIComponent(company)}${projectId ? '&project_id=' + projectId : ''}`),
    portalAcknowledge: (defectId) => axios.put(`${API}/closeout/portal/defects/${defectId}/acknowledge`),
    portalRectify: (defectId, comments) => axios.put(`${API}/closeout/portal/defects/${defectId}/rectify${comments ? '?comments=' + encodeURIComponent(comments) : ''}`),
    portalComment: (defectId, text) => axios.post(`${API}/closeout/portal/defects/${defectId}/comment?comment_text=${encodeURIComponent(text)}`),
    // Portal link management
    generatePortalLink: (data) => axios.post(`${API}/closeout/portal/generate-link`, data),
    portalLinks: (projectId) => axios.get(`${API}/closeout/portal/links/${projectId}`),
    revokePortalLink: (linkId) => axios.delete(`${API}/closeout/portal/links/${linkId}/revoke`),
    // Training
    listTrainingRecords: (projectId) => axios.get(`${API}/closeout/projects/${projectId}/training-records`),
    createTrainingRecord: (projectId, data) => axios.post(`${API}/closeout/projects/${projectId}/training-records`, data),
    completeTraining: (recordId) => axios.put(`${API}/closeout/training-records/${recordId}/complete`),
  },
  disputes: {
    // Profiles & Settings
    getProfiles: () => axios.get(`${API}/disputes/profiles`),
    getProfile: (family) => axios.get(`${API}/disputes/profiles/${family}`),
    getSettings: (projectId) => axios.get(`${API}/disputes/projects/${projectId}/settings`),
    getEnums: () => axios.get(`${API}/disputes/enums`),
    // Dashboard
    getDashboard: (projectId) => axios.get(`${API}/disputes/projects/${projectId}/dashboard`),
    // Register
    getRegister: (projectId, params = {}) => axios.get(`${API}/disputes/projects/${projectId}/register`, { params }),
    // Matters
    createMatter: (projectId, data) => axios.post(`${API}/disputes/projects/${projectId}/matters`, data),
    getMatter: (matterId) => axios.get(`${API}/disputes/matters/${matterId}`),
    updateMatter: (matterId, data) => axios.put(`${API}/disputes/matters/${matterId}`, data),
    deleteMatter: (matterId) => axios.delete(`${API}/disputes/matters/${matterId}`),
    // Issues
    listIssues: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/issues`),
    createIssue: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/issues`, data),
    updateIssue: (issueId, data) => axios.put(`${API}/disputes/issues/${issueId}`, data),
    deleteIssue: (issueId) => axios.delete(`${API}/disputes/issues/${issueId}`),
    // Notices
    listNotices: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/notices`),
    createNotice: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/notices`, data),
    updateNotice: (noticeId, data) => axios.put(`${API}/disputes/notices/${noticeId}`, data),
    deleteNotice: (noticeId) => axios.delete(`${API}/disputes/notices/${noticeId}`),
    // Procedural Steps
    listSteps: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/steps`),
    createStep: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/steps`, data),
    updateStep: (stepId, data) => axios.put(`${API}/disputes/steps/${stepId}`, data),
    // Deadlines
    listDeadlines: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/deadlines`),
    createDeadline: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/deadlines`, data),
    // Activity Log
    getActivity: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/activity`),
    // Bulk
    bulkStageChange: (data) => axios.post(`${API}/disputes/bulk/stage-change`, data),
    // Phase 2: Evidence
    listEvidence: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/evidence`),
    createEvidence: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/evidence`, data),
    updateEvidence: (evidenceId, data) => axios.put(`${API}/disputes/evidence/${evidenceId}`, data),
    deleteEvidence: (evidenceId) => axios.delete(`${API}/disputes/evidence/${evidenceId}`),
    // Phase 2: Chronology
    listChronology: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/chronology`),
    createChronologyEntry: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/chronology`, data),
    updateChronologyEntry: (entryId, data) => axios.put(`${API}/disputes/chronology/${entryId}`, data),
    deleteChronologyEntry: (entryId) => axios.delete(`${API}/disputes/chronology/${entryId}`),
    // Phase 2: Submissions
    listSubmissions: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/submissions`),
    createSubmission: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/submissions`, data),
    updateSubmission: (submissionId, data) => axios.put(`${API}/disputes/submissions/${submissionId}`, data),
    deleteSubmission: (submissionId) => axios.delete(`${API}/disputes/submissions/${submissionId}`),
    // Phase 3: Settlements
    listSettlements: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/settlements`),
    createSettlement: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/settlements`, data),
    updateSettlement: (settlementId, data) => axios.put(`${API}/disputes/settlements/${settlementId}`, data),
    deleteSettlement: (settlementId) => axios.delete(`${API}/disputes/settlements/${settlementId}`),
    // Phase 3: Decisions
    listDecisions: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/decisions`),
    createDecision: (matterId, data) => axios.post(`${API}/disputes/matters/${matterId}/decisions`, data),
    updateDecision: (decisionId, data) => axios.put(`${API}/disputes/decisions/${decisionId}`, data),
    // Phase 3: AI Analysis
    runAIAnalysis: (matterId) => axios.post(`${API}/disputes/matters/${matterId}/ai-analysis`),
    getAIAnalysis: (matterId) => axios.get(`${API}/disputes/matters/${matterId}/ai-analysis`),
    // Phase 3: Reporting
    getReport: (projectId) => axios.get(`${API}/disputes/projects/${projectId}/report`),
  },
  // Generic HTTP methods for flexibility
  get: (url, config) => axios.get(`${API}${url}`, config),
  post: (url, data, config) => axios.post(`${API}${url}`, data, config),
  put: (url, data, config) => axios.put(`${API}${url}`, data, config),
  patch: (url, data, config) => axios.patch(`${API}${url}`, data, config),
  delete: (url, config) => axios.delete(`${API}${url}`, config),
};